import { getSettings, saveSettings } from '../../../lib/cms'
import type { CmsSettings } from '../../../lib/cms'
import { isSupabaseConfigured } from '../../../lib/supabase'

const TABS = [
  { id: 'general',  label: 'Opšte',           icon: 'ri-settings-3-line' },
  { id: 'seo',      label: 'SEO',              icon: 'ri-seo-line' },
  { id: 'tracking', label: 'Tracking kodovi',  icon: 'ri-code-s-slash-line' },
  { id: 'contact',  label: 'Kontakt',          icon: 'ri-phone-line' },
]

export default function AdminSettings() {
  const [settings, setSettings] = useState<CmsSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState('general')
  const [msg, setMsg] = useState('')

  useEffect(() => {
    getSettings().then(s => { setSettings(s); setLoading(false) })
  }, [])

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3000) }

  const setField = (key: keyof CmsSettings, value: string) =>
    setSettings(prev => prev ? { ...prev, [key]: value } : prev)

  const handleSave = async () => {
    if (!settings) return
    setSaving(true)
    await saveSettings(settings)
    flash('Podešavanja sačuvana!')
    setSaving(false)
  }

  if (loading) return (
    <div className="flex items-center justify-center h-48 text-gray-400">
      <i className="ri-loader-4-line animate-spin text-2xl" />
    </div>
  )

  return (
    <div className="space-y-4">
      {msg && <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">{msg}</div>}

      {!isSupabaseConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
          <strong>Supabase nije podešen</strong> — podešavanja neće biti sačuvana trajno. Konfigurišite <code>.env</code> fajl.
        </div>
      )}

      {/* Tab navigation */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${t.icon}`} />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6 space-y-5">
          {/* OPŠTE */}
          {tab === 'general' && (
            <>
              <Field label="Naziv sajta" value={settings?.site_name} onChange={v => setField('site_name', v)} />
              <Field label="Tagline (podnaslov)" value={settings?.site_tagline} onChange={v => setField('site_tagline', v)} />
              <Field label="Opis sajta" value={settings?.site_description} onChange={v => setField('site_description', v)} textarea />
              <Field label="Canonical URL" value={settings?.canonical_url} onChange={v => setField('canonical_url', v)} placeholder="https://safeguard-systems.rs/" />
            </>
          )}

          {/* SEO */}
          {tab === 'seo' && (
            <>
              <div className="bg-blue-50 rounded-lg p-3 text-blue-800 text-xs">
                <i className="ri-information-line mr-1" />
                Ova podešavanja prepisuju default meta tagove u <code>index.html</code>. Implementacija zahteva ažuriranje <code>index.html</code> putem skripte ili SSR.
              </div>
              <Field label="Meta description" value={settings?.meta_description} onChange={v => setField('meta_description', v)} textarea placeholder="Kratak opis sajta za pretrage (max 160 karaktera)" />
              <Field label="OG Title (Open Graph)" value={settings?.og_title} onChange={v => setField('og_title', v)} />
              <Field label="OG Description" value={settings?.og_description} onChange={v => setField('og_description', v)} textarea />
              <Field label="OG Image URL" value={settings?.og_image} onChange={v => setField('og_image', v)} placeholder="https://...jpg" />
              {settings?.og_image && <img src={settings.og_image} alt="" className="h-24 rounded-lg object-cover" />}
            </>
          )}

          {/* TRACKING KODOVI */}
          {tab === 'tracking' && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <i className="ri-google-fill text-orange-500 mr-1.5" />
                    Google Analytics ID
                  </label>
                  <input
                    className="input"
                    value={settings?.google_analytics_id ?? ''}
                    onChange={e => setField('google_analytics_id', e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="text-xs text-gray-400 mt-1">Google Analytics 4 Measurement ID</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <i className="ri-google-fill text-blue-500 mr-1.5" />
                    Google Tag Manager ID
                  </label>
                  <input
                    className="input"
                    value={settings?.google_tag_manager_id ?? ''}
                    onChange={e => setField('google_tag_manager_id', e.target.value)}
                    placeholder="GTM-XXXXXXX"
                  />
                  <p className="text-xs text-gray-400 mt-1">Google Tag Manager Container ID</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <i className="ri-facebook-fill text-blue-700 mr-1.5" />
                    Facebook / Meta Pixel ID
                  </label>
                  <input
                    className="input"
                    value={settings?.facebook_pixel_id ?? ''}
                    onChange={e => setField('facebook_pixel_id', e.target.value)}
                    placeholder="1234567890123456"
                  />
                  <p className="text-xs text-gray-400 mt-1">Meta Pixel ID iz Business Managera</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <i className="ri-tiktok-fill text-gray-900 mr-1.5" />
                    TikTok Pixel ID
                  </label>
                  <input
                    className="input"
                    value={settings?.tiktok_pixel_id ?? ''}
                    onChange={e => setField('tiktok_pixel_id', e.target.value)}
                    placeholder="CXXXXXXXXXXXXXXXXXX"
                  />
                  <p className="text-xs text-gray-400 mt-1">TikTok Events Manager Pixel ID</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <i className="ri-code-s-slash-line text-gray-500 mr-1.5" />
                  Prilagođeni HEAD skripte
                </label>
                <textarea
                  className="input font-mono text-xs"
                  rows={6}
                  value={settings?.custom_head_scripts ?? ''}
                  onChange={e => setField('custom_head_scripts', e.target.value)}
                  placeholder={'<!-- Ubaciti pre </head> -->\n<script>\n  // vaš kod\n</script>'}
                />
                <p className="text-xs text-gray-400 mt-1">Ubacuje se u &lt;head&gt; — za Hotjar, Clarity, Crisp i sl.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <i className="ri-code-s-slash-line text-gray-500 mr-1.5" />
                  Prilagođeni BODY skripte
                </label>
                <textarea
                  className="input font-mono text-xs"
                  rows={6}
                  value={settings?.custom_body_scripts ?? ''}
                  onChange={e => setField('custom_body_scripts', e.target.value)}
                  placeholder={'<!-- Ubaciti pre </body> -->\n<script>\n  // vaš kod\n</script>'}
                />
                <p className="text-xs text-gray-400 mt-1">Ubacuje se na kraju &lt;body&gt; — za chat widgete, live support i sl.</p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600">
                <p className="font-medium text-slate-800 mb-1">
                  <i className="ri-information-line mr-1 text-blue-500" />
                  Kako funkcioniše ubacivanje tracking kodova
                </p>
                <p>Nakon čuvanja, <strong>TrackingCodes</strong> komponenta automatski ubacuje aktivne kodove u DOM pri učitavanju sajta. Nema potrebe za ručnim editovanjem <code>index.html</code>.</p>
              </div>
            </>
          )}

          {/* KONTAKT */}
          {tab === 'contact' && (
            <>
              <Field label="Telefon" value={settings?.phone} onChange={v => setField('phone', v)} placeholder="+381-11-000-0000" />
              <Field label="Email" value={settings?.email} onChange={v => setField('email', v)} placeholder="info@safeguard-systems.rs" />
              <Field label="Adresa" value={settings?.address} onChange={v => setField('address', v)} textarea />
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium px-6 py-2.5 rounded-lg transition"
          >
            {saving ? 'Čuvanje...' : 'Sačuvaj podešavanja'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, value, onChange, textarea = false, placeholder = '', rows = 3 }: {
  label: string
  value?: string
  onChange: (v: string) => void
  textarea?: boolean
  placeholder?: string
  rows?: number
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {textarea
        ? <textarea className="input" rows={rows} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
        : <input className="input" value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      }
    </div>
  )
}
